import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import SignatureEmailModal from '../../components/contracts/SignatureEmailModal';
import DateTimeCalendar from '../../components/ui/DateTimeCalendar';
import MagazinePageHeader from './MagazinePageHeader';
import { useAuth } from '../../hooks/useAuth';
import { getCompanys } from '../../api/companysService';
import { getSlots } from '../../api/magazineService';
import {
  addProposalLine,
  archiveProposal,
  cancelProposal,
  createProposal,
  DealActivity,
  getProposal,
  getProposalActivity,
  getProposalCompanyContacts,
  Proposal,
  removeProposalLine,
  sendProposal,
  updateProposal,
  updateProposalLine,
} from '../../api/proposalService';
import {
  Contract,
  ContractTemplate,
  generateContractFromProposal,
  getContract,
  getContractTemplates,
  getContracts,
  saveSignatureEmail,
  sendContractForSignature,
} from '../../api/contractService';
import {
  generateInvoiceFromProposal,
  getInvoices,
  MagazineInvoice,
} from '../../api/invoiceService';
import { openContractPdf } from '../../util/generateContractPdf';

const linkButtonClass =
  'inline-block rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white no-underline hover:bg-torch-primary-dark';
const controlClass =
  'mt-1.5 box-border w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-[#5949d1] focus:ring-2 focus:ring-[#5949d1]/10 disabled:bg-gray-50 disabled:text-gray-500';
const compactControlClass =
  'box-border rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 outline-none focus:border-[#5949d1] focus:ring-2 focus:ring-[#5949d1]/10 disabled:bg-gray-50 disabled:text-gray-500';
const controlLabelClass = 'block text-[13px] font-semibold text-gray-700';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  signed: 'bg-green-50 text-green-700',
  cancelled: 'bg-orange-50 text-orange-700',
  archived: 'bg-gray-100 text-gray-500',
};

const formatMoney = (amount: string | number, currency = 'gbp') => {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(n);
};

const currencies = [
  { name: 'GBP', value: 'gbp', symbol: '£' },
  { name: 'EUR', value: 'eur', symbol: '€' },
  { name: 'USD', value: 'usd', symbol: '$' },
];

const slotLabel = (slot: any) => {
  const pub = slot.issue?.publication?.name;
  const issue = slot.issue?.title;
  return [pub, issue, slot.label].filter(Boolean).join(' — ');
};

const toDateTimeLocal = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const ProposalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, canEditAnyDeal } = useAuth();
  const isNew = id === 'new';

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyContacts, setCompanyContacts] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [linkedContract, setLinkedContract] = useState<Contract | null>(null);
  const [linkedInvoice, setLinkedInvoice] = useState<MagazineInvoice | null>(null);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [sendingSign, setSendingSign] = useState(false);
  const [signatureEmailOpen, setSignatureEmailOpen] = useState(false);
  const [savingSignatureEmail, setSavingSignatureEmail] = useState(false);
  const [newLinePrice, setNewLinePrice] = useState('');
  const [newLineNotes, setNewLineNotes] = useState('');
  const [lineDraftPrices, setLineDraftPrices] = useState<Record<number, string>>({});
  const [lineDraftNotes, setLineDraftNotes] = useState<Record<number, string>>({});
  const [activity, setActivity] = useState<DealActivity[]>([]);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderDirty, setReminderDirty] = useState(false);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const [form, setForm] = useState({
    company_id: '',
    contact_name: '',
    contact_email: '',
    currency: 'gbp',
    notes: '',
    contract_from: 0,
    terms_and_conditions: '',
    remind_at: '',
  });

  const loadMeta = async () => {
    const [companyData, slotData] = await Promise.all([getCompanys(''), getSlots()]);
    setCompanies(companyData);
    setSlots(slotData);
  };

  const loadLinkedDocs = async (proposal: Proposal) => {
    const [contracts, invoices, templateData] = await Promise.all([
      getContracts(['draft', 'sent']),
      getInvoices(),
      getContractTemplates(),
    ]);
    setLinkedContract(contracts.find((c) => c.proposal_id === proposal.id) || null);
    setLinkedInvoice(invoices.find((inv) => inv.proposal_id === proposal.id) || null);
    setTemplates(templateData);
  };

  const load = async () => {
    if (!id || isNew) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getProposal(id);
      setProposal(data);
      setForm({
        company_id: data.company_id ? String(data.company_id) : '',
        contact_name: data.contact_name || '',
        contact_email: data.contact_email || '',
        currency: data.currency || 'gbp',
        notes: data.notes || '',
        contract_from: Number(data.contract_from || 0),
        terms_and_conditions: data.terms_and_conditions || '',
        remind_at: toDateTimeLocal(data.remind_at),
      });
      setReminderDirty(false);
      await loadLinkedDocs(data);
      const acts = await getProposalActivity(data.id);
      setActivity(acts);
      const prices: Record<number, string> = {};
      const notes: Record<number, string> = {};
      (data.line_items || []).forEach((line) => {
        prices[line.id] = String(line.price);
        notes[line.id] = line.notes || '';
      });
      setLineDraftPrices(prices);
      setLineDraftNotes(notes);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Proposal not found.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    load();
  }, [id]);

  useEffect(() => {
    if (!form.company_id) { setCompanyContacts([]); return; }
    let cancelled = false;
    getProposalCompanyContacts(form.company_id)
      .then((contacts) => { if (!cancelled) setCompanyContacts(contacts); })
      .catch(() => { if (!cancelled) setCompanyContacts([]); });
    return () => { cancelled = true; };
  }, [form.company_id]);

  useEffect(() => {
    if (!selectedSlotId) {
      setNewLinePrice('');
      setNewLineNotes('');
      return;
    }
    const slot = slots.find((s) => String(s.id) === selectedSlotId);
    setNewLinePrice(slot ? String(slot.price) : '');
  }, [selectedSlotId, slots]);

  const isDraft = isNew || proposal?.status === 'draft';
  const canEdit = isNew || canEditAnyDeal || proposal?.sales_rep_id === user?.id;
  const canEditDraft = isDraft && canEdit;
  const canEditLinePrice =
    canEdit &&
    proposal &&
    (proposal.status === 'draft' || (proposal.status === 'sent' && canEditAnyDeal));

  const handleCreate = async () => {
    if (reminderDirty && form.remind_at) {
      const selectedReminder = new Date(form.remind_at);
      if (Number.isNaN(selectedReminder.getTime()) || selectedReminder.getTime() <= Date.now()) {
        setReminderError('Select a valid reminder date and time in the future.');
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createProposal({
        company_id: form.company_id ? Number(form.company_id) : null,
        contact_name: form.contact_name || undefined,
        contact_email: form.contact_email || undefined,
        currency: form.currency,
        notes: form.notes || undefined,
        contract_from: form.contract_from,
        terms_and_conditions: form.terms_and_conditions || undefined,
        remind_at: form.remind_at ? new Date(form.remind_at).toISOString() : null,
      });
      setReminderDirty(false);
      navigate(`/magazine/proposals/${created.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create proposal.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHeader = async () => {
    if (!proposal) return;
    if (reminderDirty && form.remind_at) {
      const selectedReminder = new Date(form.remind_at);
      if (Number.isNaN(selectedReminder.getTime()) || selectedReminder.getTime() <= Date.now()) {
        setReminderError('Select a valid reminder date and time in the future.');
        return;
      }
    }
    setSaving(true);
    setError(null);
    setReminderError(null);
    try {
      const updated = await updateProposal(proposal.id, {
        company_id: form.company_id ? Number(form.company_id) : null,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        currency: form.currency,
        notes: form.notes,
        contract_from: form.contract_from,
        terms_and_conditions: form.terms_and_conditions,
        remind_at: form.remind_at ? new Date(form.remind_at).toISOString() : null,
      });
      setProposal(updated);
      setForm((current) => ({ ...current, remind_at: toDateTimeLocal(updated.remind_at) }));
      setReminderDirty(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLine = async () => {
    if (!proposal || !selectedSlotId) return;
    setSaving(true);
    setError(null);
    try {
      const price = newLinePrice !== '' ? Number(newLinePrice) : undefined;
      const updated = await addProposalLine(proposal.id, Number(selectedSlotId), price, newLineNotes);
      setProposal(updated);
      setSelectedSlotId('');
      setNewLinePrice('');
      setNewLineNotes('');
      const prices: Record<number, string> = {};
      const notes: Record<number, string> = {};
      (updated.line_items || []).forEach((line) => {
        prices[line.id] = String(line.price);
        notes[line.id] = line.notes || '';
      });
      setLineDraftPrices(prices);
      setLineDraftNotes(notes);
      const acts = await getProposalActivity(proposal.id);
      setActivity(acts);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to add line item.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLinePrice = async (lineId: number) => {
    if (!proposal) return;
    const price = Number(lineDraftPrices[lineId]);
    if (Number.isNaN(price)) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateProposalLine(proposal.id, lineId, price, lineDraftNotes[lineId] || '');
      setProposal(updated);
      const prices: Record<number, string> = {};
      const notes: Record<number, string> = {};
      (updated.line_items || []).forEach((line) => {
        prices[line.id] = String(line.price);
        notes[line.id] = line.notes || '';
      });
      setLineDraftPrices(prices);
      setLineDraftNotes(notes);
      const acts = await getProposalActivity(proposal.id);
      setActivity(acts);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update price.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLine = async (lineId: number) => {
    if (!proposal || !window.confirm('Remove this line item?')) return;
    setSaving(true);
    try {
      const updated = await removeProposalLine(proposal.id, lineId);
      setProposal(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to remove line.');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!proposal || !window.confirm('Send this proposal? Inventory will be held on each line.')) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await sendProposal(proposal.id);
      setProposal(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to send proposal.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!proposal || !window.confirm('Cancel this proposal?')) return;
    setSaving(true);
    try {
      const updated = await cancelProposal(proposal.id);
      setProposal(updated);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to cancel.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!proposal || !isAdmin || !window.confirm('Archive this proposal?')) return;
    await archiveProposal(proposal.id);
    navigate('/magazine/proposals');
  };

  const handleGenerateContract = async () => {
    if (!proposal) return;
    setGeneratingContract(true);
    setError(null);
    try {
      const contract = await generateContractFromProposal(
        proposal.id,
        selectedTemplateId ? Number(selectedTemplateId) : undefined
      );
      setLinkedContract(contract);
      navigate(`/contracts/${contract.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to generate contract.');
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!proposal || !linkedContract) return;
    setGeneratingInvoice(true);
    setError(null);
    try {
      const invoice = await generateInvoiceFromProposal(proposal.id, {
        contract_id: linkedContract.id,
      });
      setLinkedInvoice(invoice);
      navigate(`/invoices/${invoice.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to generate invoice.');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleViewContractPdf = async () => {
    if (!linkedContract) return;
    setPdfError(null);
    try {
      const contract = await getContract(String(linkedContract.id));
      setLinkedContract(contract);
      openContractPdf({
        ...contract,
        terms_and_conditions: contract.terms_and_conditions || form.terms_and_conditions,
      });
    } catch (e: any) {
      setPdfError(e?.response?.data?.message || e?.message || 'Unable to generate the contract PDF.');
    }
  };

  const handleSendForSignature = async (resend: boolean, ccEmail = '') => {
    if (!linkedContract) return;
    setSendingSign(true);
    setError(null);
    try {
      const updated = await sendContractForSignature(linkedContract.id, resend, undefined, ccEmail);
      setLinkedContract(updated);
      return true;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to send for signature.');
      return false;
    } finally {
      setSendingSign(false);
    }
  };

  const handleSaveSignatureEmail = async (emailSubject: string, emailText: string, ccEmail: string, sendAfterSave = false) => {
    if (!linkedContract) return;
    setSavingSignatureEmail(true);
    setError(null);
    try {
      const saved = await saveSignatureEmail(linkedContract.id, emailText, emailSubject, ccEmail);
      setLinkedContract((current) => current ? { ...current, ...saved, signature_email_subject: emailSubject, signature_email_text: emailText, email_text: emailText } : current);
      if (sendAfterSave) {
        setSavingSignatureEmail(false);
        const bl = await handleSendForSignature(linkedContract.status === 'sent', ccEmail)
        if (!bl) return;
        window.location.reload();
      }
      setSignatureEmailOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save the signature email.');
    } finally {
      setSavingSignatureEmail(false);
    }
  };

  const openSignatureEmail = async () => {
    setSignatureEmailOpen(true);
  };

  const usedSlotIds = new Set((proposal?.line_items || []).map((l) => l.ad_slot_id));
  const availableSlots = slots.filter((s) => !usedSlotIds.has(s.id));
  const selectedCompany = companies.find((company) => Number(company.id) === Number(form.company_id))
    || (proposal?.company && Number(proposal.company.id) === Number(form.company_id) ? proposal.company : null);
  const contractFromOptions = [
    { company_id: 0, company_name: 'Torch Marketing Co Ltd' },
    ...(selectedCompany
      ? [{ company_id: Number(selectedCompany.id), company_name: selectedCompany.name }]
      : []),
  ];

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isNew ? 'New proposal' : `Proposal #${proposal?.id || ''}`}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isNew ? 'Create a draft and add advertising line items.' : proposal?.company?.name || 'Magazine proposal'}
          </p>
        </div>
        <Link to="/magazine/proposals" className={linkButtonClass}>
          Back to proposals
        </Link>
      </div>

      {loading ? (
        <PageCard><p className="text-sm text-gray-500">Loading...</p></PageCard>
      ) : (
        <>
          <PageCard className="mb-6">
            <h2 className="mb-4 text-sm font-semibold text-gray-900">Proposal details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={controlLabelClass}>Company</label>
                <select
                  className={controlClass}
                  value={form.company_id}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value, contact_name: '', contact_email: '', contract_from: 0 })}
                  disabled={!canEditDraft || saving}
                >
                  <option value="">Select company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={controlLabelClass}>Contact name</label>
                <select className={controlClass} value={companyContacts.find((c) => [c.first_name, c.last_name].filter(Boolean).join(' ') === form.contact_name && (c.email || '') === form.contact_email)?.id || ''} onChange={(e) => { const contact = companyContacts.find((c) => String(c.id) === e.target.value); setForm({ ...form, contact_name: contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : '', contact_email: contact?.email || '' }); }} disabled={!canEditDraft || saving || !form.company_id}>
                  <option value="">{form.company_id ? 'Select contact' : 'Select a company first'}</option>
                  {companyContacts.map((contact) => <option key={contact.id} value={contact.id}>{[contact.first_name, contact.last_name].filter(Boolean).join(' ')}{contact.job_title ? ` — ${contact.job_title}` : ''}</option>)}
                </select>
                {form.company_id && companyContacts.length === 0 && <p className="mt-1 text-xs text-amber-700">Add a contact to this company before creating a proposal.</p>}
              </div>
              <div>
                <label className={controlLabelClass}>Contact email</label>
                <div className="w-full flex">
                  <input
                    type="email"
                    className={`${controlClass} min-w-0 flex-1`}
                    value={form.contact_email}
                    readOnly
                    disabled={!canEditDraft || saving}
                  />
                </div>
              </div>
              <div>
                <label className={controlLabelClass}>Currency</label>
                <select
                  className={controlClass}
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  disabled={!canEditDraft || saving}
                >
                  {currencies.map((currency) => (
                    <option key={currency.value} value={currency.value}>
                      {currency.symbol} ({currency.name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={controlLabelClass}>Contract From</label>
                <select
                  className={controlClass}
                  value={form.contract_from}
                  onChange={(e) => setForm({ ...form, contract_from: Number(e.target.value) })}
                  disabled={!canEditDraft || saving}
                >
                  {contractFromOptions.map((company) => (
                    <option key={company.company_id} value={company.company_id}>{company.company_name}</option>
                  ))}
                </select>
              </div>
              {!isNew && proposal && (
                <div>
                  <label className={controlLabelClass}>Status</label>
                  <span className={`inline-block rounded px-2 py-0.5 text-sm capitalize ${statusClass[proposal.archived_at ? 'archived' : proposal.status] || ''}`}>
                    {proposal.archived_at ? 'archived' : proposal.status}
                  </span>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className={controlLabelClass}>Notes</label>
                <div className="w-full flex">
                  <textarea
                    rows={3}
                    className={`${controlClass} min-w-0 flex-1 resize-y`}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={!canEditDraft || saving}
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={controlLabelClass}>Terms &amp; Conditions</label>
                <textarea
                  rows={10}
                  className={`${controlClass} resize-y`}
                  value={form.terms_and_conditions}
                  onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })}
                  placeholder="Add the Terms & Conditions for this contract."
                  disabled={!canEditDraft || saving}
                />
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setReminderOpen(true)}
                  disabled={!canEditDraft || saving}
                  className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                >
                  {form.remind_at ? 'Change reminder' : 'Set reminder'}
                </button>

                {form.remind_at && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setForm({ ...form, remind_at: '' });
                        setReminderDirty(true);
                        setReminderError(null);
                      }}
                      disabled={!canEditDraft || saving}
                      className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Clear reminder
                    </button>
                    <span className={`rounded px-2 py-1 text-xs font-medium ${new Date(form.remind_at).getTime() < Date.now() ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                      {new Date(form.remind_at).getTime() < Date.now() ? 'Passed' : 'Upcoming'}
                      {' · '}
                      {new Date(form.remind_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </>
                )}
              </div>

              {reminderOpen && (
                <div className="mt-3 max-w-sm">
                  <label className={controlLabelClass}>Remind date and time</label>
                  <DateTimeCalendar
                    value={form.remind_at}
                    onClose={() => setReminderOpen(false)}
                    onChange={(value) => {
                      setForm({ ...form, remind_at: value });
                      setReminderDirty(true);
                      setReminderError(null);
                    }}
                    disabled={!canEditDraft || saving}
                  />
                  {reminderError && <p className="mt-1 text-sm text-red-600">{reminderError}</p>}
                  {reminderDirty && !reminderError && (
                    <p className="mt-1 text-xs text-gray-500">Click Save details to apply this reminder.</p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {isNew ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={saving}
                  className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                >
                  Create proposal
                </button>
              ) : canEditDraft ? (
                <button
                  type="button"
                  onClick={handleSaveHeader}
                  disabled={saving}
                  className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                >
                  Save details
                </button>
              ) : null}
              {error && <span className="text-sm text-red-600">{error}</span>}
            </div>
          </PageCard>

          {!isNew && proposal && (
            <PageCard className="mb-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Line items</h2>
                <span className="text-sm font-medium text-gray-700">
                  Total: {formatMoney(proposal.total_price || 0, form.currency)}
                </span>
              </div>

              {canEditDraft && (
                <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="min-w-[200px] max-w-[300px] flex-1">
                    <label className={controlLabelClass}>Add ad slot</label>
                    <select
                      className={controlClass}
                      value={selectedSlotId}
                      onChange={(e) => setSelectedSlotId(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Select inventory</option>
                      {availableSlots.map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {slotLabel(slot)} ({formatMoney(slot.price, form.currency)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-[120px] max-w-[200px]">
                    <label className={controlLabelClass}>Negotiated price</label>
                    <div className="w-full flex">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${controlClass} min-w-0 flex-1`}
                        value={newLinePrice}
                        onChange={(e) => setNewLinePrice(e.target.value)}
                        disabled={saving}
                        placeholder="Rate card"
                      />
                    </div>
                  </div>
                  <div className="min-w-[220px] flex-1">
                    <label className={controlLabelClass}>Notes</label>
                    <div className="flex w-full">
                      <input
                        className={`${controlClass} min-w-0 flex-1`}
                        value={newLineNotes}
                        onChange={(e) => setNewLineNotes(e.target.value)}
                        disabled={saving}
                        placeholder="Optional line item notes"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddLine}
                    disabled={saving || !selectedSlotId}
                    className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                  >
                    Add item
                  </button>
                </div>
              )}

              {!proposal.line_items?.length ? (
                <p className="py-6 text-center text-sm text-gray-500">No line items yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="py-2 pr-4">Publication / Issue</th>
                        <th className="py-2 pr-4">Ad slot</th>
                        <th className="py-2 pr-4">Size</th>
                        <th className="py-2 pr-4">Price</th>
                        <th className="py-2 pr-4">Notes</th>
                        {(canEditDraft || canEditLinePrice) && <th className="py-2">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {proposal.line_items.map((line) => (
                        <tr key={line.id} className="border-b border-gray-100">
                          <td className="py-3 pr-4 text-gray-900">
                            {line.ad_slot?.issue?.publication?.name || '—'}
                            <br />
                            <span className="text-gray-500">{line.ad_slot?.issue?.title || '—'}</span>
                          </td>
                          <td className="py-3 pr-4 text-gray-900">{line.label}</td>
                          <td className="py-3 pr-4 text-gray-600">{line.size || '—'}</td>
                          <td className="py-3 pr-4 text-gray-900">
                            {canEditLinePrice ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className={`${compactControlClass} w-24`}
                                  value={lineDraftPrices[line.id] ?? String(line.price)}
                                  onChange={(e) =>
                                    setLineDraftPrices({ ...lineDraftPrices, [line.id]: e.target.value })
                                  }
                                  disabled={saving}
                                />
                              </div>
                            ) : (
                              formatMoney(line.price, form.currency)
                            )}
                          </td>
                          <td className="py-3 pr-4 text-gray-900">
                            {canEditLinePrice ? (
                              <input
                                className={`${compactControlClass} min-w-0 w-full`}
                                value={lineDraftNotes[line.id] ?? line.notes ?? ''}
                                onChange={(e) =>
                                  setLineDraftNotes({ ...lineDraftNotes, [line.id]: e.target.value })
                                }
                                disabled={saving}
                              />
                            ) : (
                              line.notes || '—'
                            )}
                          </td>
                          {(canEditDraft || canEditLinePrice) && (
                            <td className="py-3">
                              <div className="flex items-center gap-3">
                                {canEditLinePrice &&
                                  lineDraftPrices[line.id] !== undefined &&
                                  (Number(lineDraftPrices[line.id]) !== Number(line.price) ||
                                    (lineDraftNotes[line.id] || '') !== (line.notes || '')) && (
                                    <button
                                      type="button"
                                      onClick={() => handleSaveLinePrice(line.id)}
                                      className="text-sm font-medium text-torch-primary hover:underline"
                                      disabled={saving}
                                    >
                                      Save
                                    </button>
                                  )}
                                {canEditDraft && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveLine(line.id)}
                                    className="text-sm text-red-600 hover:underline"
                                    disabled={saving}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2 border-t border-gray-200 pt-4">
                {canEdit && proposal.status === 'draft' && (
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={saving || !proposal.line_items?.length}
                    className="rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                  >
                    Mark as sent (hold inventory)
                  </button>
                )}
                {canEdit && ['draft', 'sent'].includes(proposal.status) && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className="rounded border border-orange-200 px-3 py-1.5 text-sm text-orange-800 hover:bg-orange-50 disabled:opacity-50"
                  >
                    Cancel proposal
                  </button>
                )}
                {isAdmin && !proposal.archived_at && (
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={saving}
                    className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Archive
                  </button>
                )}
                {proposal.company && (
                  <Link
                    to={`/companys/${proposal.company.id}/overview`}
                    className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 no-underline hover:bg-gray-50"
                  >
                    Company overview
                  </Link>
                )}
              </div>
            </PageCard>
          )}

          {!isNew && proposal && (
            <PageCard>
              <h2 className="mb-4 text-sm font-semibold text-gray-900">Contract &amp; invoice</h2>

              <div className="space-y-4">
                <div>
                  <div className="mb-1 text-sm font-medium text-gray-600">Contract</div>
                  {linkedContract ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/contracts/${linkedContract.id}`}
                        className="text-sm text-torch-primary hover:underline"
                      >
                        View contract
                      </Link>
                      <button
                        type="button"
                        onClick={handleViewContractPdf}
                        className="rounded border border-torch-primary bg-white px-2 py-1 text-xs font-medium text-torch-primary hover:bg-purple-50"
                      >
                        View contract as PDF
                      </button>
                      <span className={`rounded px-2 py-0.5 text-xs capitalize ${statusClass[linkedContract.status] || ''}`}>
                        {linkedContract.status}
                      </span>
                      {canEdit && linkedContract.status !== 'signed' && (
                        <button
                          type="button"
                          onClick={openSignatureEmail}
                          disabled={sendingSign}
                          className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                        >
                          {linkedContract.status === 'sent' ? 'Resend for e-signature' : 'Review & send for e-signature'}
                        </button>
                      )}
                      {canEdit && linkedContract.status !== 'signed' && (
                        <button type="button" onClick={openSignatureEmail} className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                          View/edit email
                        </button>
                      )}
                    </div>
                  ) : canEdit && proposal.line_items?.length && !['cancelled', 'archived'].includes(proposal.status) && !proposal.archived_at ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[200px]">
                        <label className={controlLabelClass}>Template (optional)</label>
                        <select
                          className={controlClass}
                          value={selectedTemplateId}
                          onChange={(e) => setSelectedTemplateId(e.target.value)}
                          disabled={generatingContract}
                        >
                          <option value="">Auto-select by publication</option>
                          {templates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.publication?.name ? ` (${t.publication.name})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateContract}
                        disabled={generatingContract}
                        className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {generatingContract ? 'Generating…' : 'Generate contract'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Add line items to generate a contract.</p>
                  )}
                  {pdfError && <p className="mt-2 text-sm text-red-600">{pdfError}</p>}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="mb-1 text-sm font-medium text-gray-600">Invoice</div>
                  {linkedInvoice ? (
                    <Link
                      to={`/invoices/${linkedInvoice.id}`}
                      className="text-sm text-torch-primary hover:underline"
                    >
                      View invoice {linkedInvoice.invoice_number}
                    </Link>
                  ) : canEdit && linkedContract?.status === 'signed' ? (
                    <button
                      type="button"
                      onClick={handleGenerateInvoice}
                      disabled={generatingInvoice}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {generatingInvoice ? 'Creating…' : 'Generate invoice'}
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Invoice available after the contract is signed.
                    </p>
                  )}
                </div>
              </div>
            </PageCard>
          )}

          {!isNew && proposal && activity.length > 0 && (
            <PageCard className="mt-6">
              <h2 className="mb-4 text-sm font-semibold text-gray-900">Activity</h2>
              <ul className="space-y-3">
                {activity.map((entry) => (
                  <li key={entry.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <div className="text-xs text-gray-500">
                      {new Date(entry.createdAt).toLocaleString()}
                      {' · '}
                      {entry.user?.username || entry.user?.email || 'System'}
                    </div>
                    <div className="mt-0.5 text-sm text-gray-800">{entry.summary}</div>
                  </li>
                ))}
              </ul>
            </PageCard>
          )}
          {linkedContract && (
            <SignatureEmailModal
              open={signatureEmailOpen}
              initialSubject={''}
              initialText={linkedContract.signature_email_text || linkedContract.default_signature_email || ''}
              recipientEmail={proposal?.contact_email || linkedContract.signer_email || ''}
              contacts={companyContacts}
              initialCcEmail={linkedContract.signature_email_cc || ''}
              saving={savingSignatureEmail}
              sending={sendingSign}
              status={linkedContract.status}
              onClose={() => setSignatureEmailOpen(false)}
              onSave={(subject, text, ccEmail) => handleSaveSignatureEmail(subject, text, ccEmail)}
              onSaveAndSend={(subject, text, ccEmail) => handleSaveSignatureEmail(subject, text, ccEmail, true)}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default ProposalDetail;
